import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Check, X, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { WildfireValues } from './WildfireSchema';

// Legacy JS api module — typed via the permissive @/api/* shim.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as fpaMembersApi from '@/api/fpaMembers';

interface MemberRecord {
  id: number;
  name: string;
  ref_number?: string | null;
  member_type?: 'member' | 'non_member' | null;
}

/**
 * Member / non-member chip.
 *
 * Non-members are ordinary property records that simply don't pay — the
 * picker searches both because whoever started an illegal burn is at least as
 * likely to be one — so every row is labelled rather than only the unusual
 * one. An unlabelled row would otherwise read as "member", which is the wrong
 * thing to attach to a fire.
 */
function TypeChip({ type }: { type?: string | null }) {
  const isNonMember = type === 'non_member';
  return (
    <Badge variant={isNonMember ? 'secondary' : 'outline'} className="shrink-0 text-[10px]">
      {isNonMember ? 'Non-member' : 'Member'}
    </Badge>
  );
}

/**
 * Single-select picker that optionally links a wildfire to an FPA member
 * (e.g. the party responsible for an illegal burn). Writes `memberId` +
 * `memberName` on the wildfire form. Shown on every fire — the link is optional.
 *
 * Searches members and non-members alike. The list endpoint defaults to
 * members only, so it must be asked for `all` explicitly; without that a
 * non-member offender simply could not be recorded.
 */
export function MemberOffenderPicker() {
  const form = useFormContext<WildfireValues>();
  const memberId = form.watch('memberId') as number | null | undefined;
  const memberName = (form.watch('memberName') ?? '') as string;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemberRecord[]>([]);
  const [loading, setLoading] = useState(false);
  // The form stores only id + name, so the type is remembered here. On an
  // edit-load it is unknown until the picker is opened, and the chip is left
  // off rather than guessed — a wrong "Member" label on an offender is worse
  // than no label.
  const [selectedType, setSelectedType] = useState<MemberRecord['member_type']>(null);

  const selectedTypeKnown =
    selectedType ?? results.find((m) => m.id === memberId)?.member_type ?? null;

  // Debounced search against the tenant's members.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        // 'all' is the query-only value that lifts the filter — omitting the
        // parameter selects members only (the endpoint's default).
        const params: Record<string, unknown> = {
          page: 1,
          page_size: 25,
          member_type: 'all',
        };
        if (query.trim()) params.search = query.trim();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = (await (fpaMembersApi as any).listMembers(params)) as {
          members?: MemberRecord[];
        };
        if (cancelled) return;
        setResults(data.members || []);
      } catch (err) {
        console.warn('Failed to list members for member picker:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  const select = (m: MemberRecord) => {
    form.setValue('memberId', m.id, { shouldDirty: true, shouldValidate: true });
    form.setValue('memberName', m.name, { shouldDirty: true });
    setSelectedType(m.member_type ?? null);
    setOpen(false);
  };

  const clear = () => {
    form.setValue('memberId', null, { shouldDirty: true, shouldValidate: true });
    form.setValue('memberName', '', { shouldDirty: true });
    setSelectedType(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="gap-1">
              <Search className="h-4 w-4" />
              {memberId ? 'Change member' : 'Link member'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <div className="border-b border-border p-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or ref…"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto">
              {loading && results.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                  Loading…
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                  No members or non-members match.
                </div>
              ) : (
                <ul>
                  {results.map((m) => {
                    const active = memberId === m.id;
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          onClick={() => select(m)}
                          className={cn(
                            'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted',
                            active && 'bg-primary/5',
                          )}
                        >
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="flex-1 truncate">
                            {m.name}
                            {m.ref_number ? (
                              <span className="text-muted-foreground"> · #{m.ref_number}</span>
                            ) : null}
                          </span>
                          <TypeChip type={m.member_type} />
                          {active && <Check className="h-4 w-4 text-primary" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {memberId ? (
          <Badge variant="default" className="gap-1.5 pl-2 pr-1">
            {memberName || `#${memberId}`}
            {selectedTypeKnown === 'non_member' && (
              <span className="text-[10px] opacity-80">· Non-member</span>
            )}
            <button
              type="button"
              onClick={clear}
              className="rounded-full p-0.5 hover:bg-primary-foreground/20"
              aria-label="Remove"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ) : (
          <span className="text-sm text-muted-foreground">No member linked</span>
        )}
      </div>
    </div>
  );
}

export default MemberOffenderPicker;
